import { last } from 'lodash';
import { RouteLocation } from 'vue-router';

const STORAGE_KEY = 'navStack';
const MAX_HISTORY_SIZE = 10;

// cleanup legacy entries
sessionStorage.removeItem('history');
sessionStorage.removeItem('history_v3');

class NavigationService {
  private constructor(
    // stack where last entry = current logical parent path
    private readonly pathStack: Array<string> = [],
  ) {
  }

  handleNavigation(from: RouteLocation, to: RouteLocation): void {
    // navigation from child to parent
    // TODO validate that paths always match exactly
    // TODO evaluated afterEach, so redirects might have happened
    if (to.fullPath === this.getParentPath()) {
      this.pathStack.pop();
      return this.save();
    }

    // navigation within same module = do nothing
    const fromModule = this.getModuleName(from);
    const toModule = this.getModuleName(to);
    if (fromModule === toModule) {
      return;
    }

    // navigation from parent to child
    if (fromModule && this.getParents(to)?.includes(fromModule)) {
      this.pathStack.push(from.fullPath);
      if (this.pathStack.length > MAX_HISTORY_SIZE) this.pathStack.splice(0, this.pathStack.length - MAX_HISTORY_SIZE);
      return this.save();
    }

    // unrelated navigation = clear stack
    this.pathStack.splice(0);
    this.save();
  }

  getParentPath(): string | undefined {
    return last(this.pathStack);
  }

  private getModuleName(route: RouteLocation): string | undefined {
    const name = route.meta.logicalModule;
    if (!name) return;
    if (typeof name === 'string') return name;
    console.error('invalid logicalModule defined for ', route.path); // eslint-disable-line no-console
  }

  private getParents(route: RouteLocation): Array<string> | undefined {
    const parents = route.meta.logicalParents;
    if (!parents) return;
    if (Array.isArray(parents) && parents.length > 0 && parents.every(name => typeof name === 'string')) return parents;
    console.error('invalid logicalParents defined for ', route.path); // eslint-disable-line no-console
  }

  static load(): NavigationService {
    const json = sessionStorage.getItem(STORAGE_KEY);
    const paths = json ? JSON.parse(json) : [];
    return new NavigationService(paths);
  }

  private save(): void {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.pathStack));
  }
}

export default NavigationService.load();